import { useId, useState } from 'react';
import { parseAmountToCents, type Cents } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';

export interface PriceInputProps {
  label: string;
  valueCents: Cents | null;
  onChange(cents: Cents | null, valid: boolean): void;
  disabled?: boolean;
}

/**
 * Same parsing contract as the shared `MoneyInput`
 * (packages/admin/src/components/MoneyInput.tsx) — every keystroke goes
 * through `@equinox/shared`'s `parseAmountToCents` — but rendered with an
 * explicit "$ " prefix inside the field, per this feature's house rule for
 * money inputs. Kept as its own component rather than an edit to the shared
 * one: `MoneyInput` is not part of this piece of work's owned files, and its
 * existing call site (the inline price edit on the products table) is left
 * exactly as it was.
 */
export function PriceInput({ label, valueCents, onChange, disabled }: PriceInputProps) {
  const { t } = useI18n();
  const id = useId();
  const [raw, setRaw] = useState(() => (valueCents == null ? '' : (valueCents / 100).toFixed(2)));
  const [touched, setTouched] = useState(false);

  const cents = raw.trim() === '' ? null : parseAmountToCents(raw);
  const invalid = touched && raw.trim() !== '' && cents === null;

  return (
    <label htmlFor={id}>
      {label}
      <div style={{ position: 'relative' }}>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 'var(--space-3)',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
            pointerEvents: 'none',
          }}
        >
          $
        </span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          placeholder="0.00"
          value={raw}
          disabled={disabled}
          aria-invalid={invalid}
          className="numeric"
          style={{ paddingLeft: '1.5rem', textAlign: 'right', width: '100%' }}
          onChange={(e) => {
            const next = e.target.value;
            setRaw(next);
            setTouched(true);
            const parsed = next.trim() === '' ? null : parseAmountToCents(next);
            onChange(parsed, next.trim() === '' || parsed !== null);
          }}
        />
      </div>
      {invalid ? (
        <span className="field-error">{t('products.priceInvalid')}</span>
      ) : (
        <span className="field-hint numeric">{cents == null ? ' ' : t('products.priceCentsPreview', { cents })}</span>
      )}
    </label>
  );
}
